import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  SpinnerGap,
  Upload,
  Palette,
  CheckCircle,
  Buildings,
  Check,
  X,
  Warning,
  Image as ImageIcon,
  Eye,
  Desktop,
  DeviceMobile,
} from "phosphor-react";
import { authFetch, authFetchJson, authUploadFile } from "../auth/authApi";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * BrandKitWizard - Setup wizard for brand kit (logo, colors, footer)
 * UNIFIED: Uses existing Branding tab data if available
 * 
 * FIX JAN 2026: 
 * - Logo rendering: properly detect and display existing tenant logo
 * - Color selection: explicit primary vs accent role selection
 * - Error handling: surface logo load errors
 */
export default function BrandKitWizard({ onComplete, initialBrandKit = null }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Brand kit state
  const [logoUrl, setLogoUrl] = useState(initialBrandKit?.logo_url || null);
  const [logoError, setLogoError] = useState(null); // Track logo load errors
  const [extractedPalette, setExtractedPalette] = useState(initialBrandKit?.extracted_palette || []);
  const [primaryColor, setPrimaryColor] = useState(initialBrandKit?.primary_color || "#FF6600");
  const [accentColor, setAccentColor] = useState(initialBrandKit?.accent_color || "#333333");
  const [businessName, setBusinessName] = useState(initialBrandKit?.business_name || "");
  const [footerAddress, setFooterAddress] = useState(initialBrandKit?.footer_address || "");
  const [footerText, setFooterText] = useState(initialBrandKit?.footer_text || "");
  
  // Color editing mode: 'primary' or 'accent'
  const [activeColorRole, setActiveColorRole] = useState('primary');
  
  const fileInputRef = useRef(null);
  
  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;
  
  // Handle palette color selection - applies to active role
  const handlePaletteColorClick = (color) => {
    if (activeColorRole === 'primary') {
      setPrimaryColor(color);
    } else {
      setAccentColor(color);
    }
  };
  
  // Get the currently active color value
  const activeColorValue = activeColorRole === 'primary' ? primaryColor : accentColor;
  
  // Handle logo image load error
  const handleLogoError = () => {
    setLogoError("Failed to load logo image");
  };
  
  // Handle logo image load success
  const handleLogoLoad = () => {
    setLogoError(null);
  };

  // Use tenant_logo as fallback if brand kit override not set
  useEffect(() => {
    const checkExistingBranding = async () => {
      try {
        // Brand Kit endpoint reads from tenants collection
        const brandData = await authFetchJson('/tenants/brand-kit');
        
        if (brandData.brand_kit) {
          const bk = brandData.brand_kit;
          
          // Effective logo: brand kit logo_url OR fallback to canonical tenant_logo
          const effectiveLogo = bk.logo_url || brandData.tenant_logo;
          
          if (effectiveLogo) {
            setLogoUrl(effectiveLogo);
          }
          
          // Extracted palette from logo
          if (bk.extracted_palette && bk.extracted_palette.length > 0) {
            setExtractedPalette(bk.extracted_palette);
          }
          
          // Colors - use tenant values
          if (bk.primary_color) setPrimaryColor(bk.primary_color);
          if (bk.accent_color) setAccentColor(bk.accent_color);
          
          // Business info
          if (bk.business_name) setBusinessName(bk.business_name);
          if (bk.footer_address) setFooterAddress(bk.footer_address);
          if (bk.footer_text) setFooterText(bk.footer_text);
          
          // If ANY effective logo exists, skip to step 2 (colors)
          if (effectiveLogo) {
            setStep(2);
          }
        }
        
        // Also get tenant info for company name fallback
        try {
          const tenantData = await authFetchJson('/tenants/current');
          if (tenantData.company_name && !businessName) {
            setBusinessName(tenantData.company_name);
          }
        } catch {
          // Ignore tenant info fetch errors - not critical
        }
      } catch (error) {
        // Handle API errors gracefully - don't block setup
        if (!error.message?.includes("403")) {
          toast.error("Failed to load existing branding data");
        }
      } finally {
        setInitialLoading(false);
      }
    };
    
    checkExistingBranding();
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }
    
    setLoading(true);
    
    try {
      // Use authUploadFile for Bearer token authenticated file upload
      const data = await authUploadFile('/tenants/brand-kit/logo', file);
      
      setLogoUrl(data.logo_url);
      setExtractedPalette(data.extracted_palette || []);
      
      // Apply suggested colors
      if (data.suggested_colors) {
        setPrimaryColor(data.suggested_colors.primary);
        setAccentColor(data.suggested_colors.accent);
      }
      
      toast.success("Logo uploaded! Colors extracted from your logo.");
    } catch (error) {
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setLoading(false);
    }
  };

  const saveColors = async () => {
    setLoading(true);
    try {
      // Use authFetch for Bearer token authenticated PUT request
      const response = await authFetch('/tenants/brand-kit', {
        method: 'PUT',
        body: JSON.stringify({
          primary_color: primaryColor,
          accent_color: accentColor,
        }),
      });
      
      if (response.ok) {
        toast.success("Colors saved!");
        setStep(3);
      } else {
        toast.error("Failed to save colors");
      }
    } catch (error) {
      toast.error("Failed to save colors");
    } finally {
      setLoading(false);
    }
  };

  const saveFooter = async () => {
    setLoading(true);
    try {
      // Use authFetch for Bearer token authenticated PUT request
      const response = await authFetch('/tenants/brand-kit', {
        method: 'PUT',
        body: JSON.stringify({
          business_name: businessName,
          footer_address: footerAddress,
          footer_text: footerText,
        }),
      });
      
      if (response.ok) {
        toast.success("Footer saved!");
        setStep(4);
      } else {
        toast.error("Failed to save footer");
      }
    } catch (error) {
      toast.error("Failed to save footer");
    } finally {
      setLoading(false);
    }
  };

  const generateTemplates = async () => {
    setLoading(true);
    try {
      // Use authFetchJson for Bearer token authenticated POST request
      const data = await authFetchJson('/tenants/marketing-templates/generate', {
        method: 'POST',
      });
      
      toast.success(`${data.templates_created} templates created!`);
      onComplete?.();
    } catch (error) {
      toast.error(error.message || "Failed to generate templates");
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FF6600]/10 flex items-center justify-center mx-auto mb-4">
                <Upload size={32} className="text-[#FF6600]" />
              </div>
              <h2 className="text-xl font-bold mb-2">Upload Your Logo</h2>
              <p className="text-zinc-500">
                We&apos;ll extract colors from your logo to create a consistent brand palette
              </p>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              data-testid="logo-file-input"
            />
            
            {logoUrl ? (
              <div className={`border-2 rounded-lg p-6 text-center ${logoError ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
                <div className="w-24 h-24 mx-auto mb-4 bg-white rounded-lg border-2 border-zinc-200 flex items-center justify-center overflow-hidden">
                  <img 
                    src={logoUrl} 
                    alt="Company Logo" 
                    className="max-w-full max-h-full object-contain"
                    onError={handleLogoError}
                    onLoad={handleLogoLoad}
                    data-testid="brand-kit-logo"
                  />
                </div>
                {logoError ? (
                  <>
                    <Badge className="bg-red-100 text-red-800">
                      <Warning size={14} className="mr-1" />
                      {logoError}
                    </Badge>
                    <p className="text-xs text-red-600 mt-2">URL: {logoUrl}</p>
                  </>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-800">
                    <CheckCircle size={14} className="mr-1" />
                    Logo uploaded
                  </Badge>
                )}
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2"
                    data-testid="change-logo-btn"
                  >
                    {logoError ? 'Upload New Logo' : 'Change Logo'}
                  </Button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#FF6600] hover:bg-[#FF6600]/5 transition-all"
                data-testid="upload-logo-area"
              >
                <ImageIcon size={48} className="mx-auto text-zinc-400 mb-4" />
                <p className="font-medium mb-1">Click to upload logo</p>
                <p className="text-sm text-zinc-500">PNG, JPG, or SVG (max 5MB)</p>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4">
              <Button
                onClick={() => setStep(2)}
                disabled={!logoUrl || loading || logoError}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                data-testid="next-colors-btn"
              >
                {loading ? (
                  <SpinnerGap size={18} className="animate-spin" />
                ) : (
                  <>
                    Next: Colors
                    <ArrowRight size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FF6600]/10 flex items-center justify-center mx-auto mb-4">
                <Palette size={32} className="text-[#FF6600]" />
              </div>
              <h2 className="text-xl font-bold mb-2">Choose Your Colors</h2>
              <p className="text-zinc-500">
                Select which color role to edit, then pick from the palette
              </p>
            </div>
            
            {/* Color Role Selector */}
            <div className="p-3 bg-zinc-100 rounded-lg">
              <Label className="mb-2 block text-xs text-zinc-500 uppercase tracking-wide">
                Editing
              </Label>
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveColorRole('primary')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    activeColorRole === 'primary'
                      ? 'border-[#FF6600] bg-white shadow-sm'
                      : 'border-transparent bg-zinc-50 hover:bg-white'
                  }`}
                  data-testid="select-primary-role"
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded border-2 border-zinc-300"
                      style={{ backgroundColor: primaryColor }}
                    />
                    <div className="text-left">
                      <p className="font-medium text-sm">Primary</p>
                      <p className="text-xs text-zinc-500">{primaryColor}</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setActiveColorRole('accent')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    activeColorRole === 'accent'
                      ? 'border-[#FF6600] bg-white shadow-sm'
                      : 'border-transparent bg-zinc-50 hover:bg-white'
                  }`}
                  data-testid="select-accent-role"
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded border-2 border-zinc-300"
                      style={{ backgroundColor: accentColor }}
                    />
                    <div className="text-left">
                      <p className="font-medium text-sm">Accent</p>
                      <p className="text-xs text-zinc-500">{accentColor}</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Extracted Palette - applies to active role */}
            {extractedPalette.length > 0 && (
              <div>
                <Label className="mb-2 block">
                  Colors from your logo
                  <span className="text-xs text-zinc-400 ml-2">
                    (click to set as {activeColorRole})
                  </span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {extractedPalette.map((color, idx) => (
                    <button
                      key={idx}
                      onClick={() => handlePaletteColorClick(color)}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        activeColorValue === color 
                          ? "border-black scale-110 shadow-lg" 
                          : "border-zinc-200 hover:border-zinc-400 hover:scale-105"
                      }`}
                      style={{ backgroundColor: color }}
                      title={`Set ${activeColorRole} to ${color}`}
                      data-testid={`palette-color-${idx}`}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Primary Color */}
            <div className={`p-3 rounded-lg border-2 transition-all ${
              activeColorRole === 'primary' ? 'border-[#FF6600] bg-[#FF6600]/5' : 'border-zinc-200'
            }`}>
              <Label className="mb-2 block flex items-center gap-2">
                Primary Color
                {activeColorRole === 'primary' && (
                  <Badge className="bg-[#FF6600] text-white text-xs">Editing</Badge>
                )}
              </Label>
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg border-2 border-zinc-300 cursor-pointer"
                  style={{ backgroundColor: primaryColor }}
                  onClick={() => setActiveColorRole('primary')}
                />
                <Input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  onFocus={() => setActiveColorRole('primary')}
                  className="w-32 font-mono"
                  placeholder="#FF6600"
                  data-testid="primary-color-input"
                />
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  onFocus={() => setActiveColorRole('primary')}
                  className="w-10 h-10 p-0 border-0 cursor-pointer"
                  data-testid="primary-color-picker"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">Used for headers, buttons, and accents</p>
            </div>
            
            {/* Accent Color */}
            <div className={`p-3 rounded-lg border-2 transition-all ${
              activeColorRole === 'accent' ? 'border-[#FF6600] bg-[#FF6600]/5' : 'border-zinc-200'
            }`}>
              <Label className="mb-2 block flex items-center gap-2">
                Accent Color
                {activeColorRole === 'accent' && (
                  <Badge className="bg-[#FF6600] text-white text-xs">Editing</Badge>
                )}
              </Label>
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg border-2 border-zinc-300 cursor-pointer"
                  style={{ backgroundColor: accentColor }}
                  onClick={() => setActiveColorRole('accent')}
                />
                <Input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  onFocus={() => setActiveColorRole('accent')}
                  className="w-32 font-mono"
                  placeholder="#333333"
                  data-testid="accent-color-input"
                />
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  onFocus={() => setActiveColorRole('accent')}
                  className="w-10 h-10 p-0 border-0 cursor-pointer"
                  data-testid="accent-color-picker"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">Used for secondary elements and gradients</p>
            </div>
            
            {/* Preview */}
            <div className="p-4 bg-zinc-50 rounded-lg">
              <p className="text-xs text-zinc-500 mb-3">Color Preview</p>
              <div className="flex items-center gap-3">
                <div
                  className="h-10 flex-1 rounded"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}
                />
                <div
                  className="px-4 py-2 rounded text-sm font-medium"
                  style={{ backgroundColor: primaryColor, color: "#fff" }}
                >
                  Primary
                </div>
                <div
                  className="px-4 py-2 rounded text-sm font-medium"
                  style={{ backgroundColor: accentColor, color: "#fff" }}
                >
                  Accent
                </div>
              </div>
            </div>
            
            <div className="flex justify-between gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="border-2"
              >
                <ArrowLeft size={18} className="mr-2" />
                Back
              </Button>
              <Button
                onClick={saveColors}
                disabled={loading}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
              >
                {loading ? (
                  <SpinnerGap size={18} className="animate-spin" />
                ) : (
                  <>
                    Next: Footer
                    <ArrowRight size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FF6600]/10 flex items-center justify-center mx-auto mb-4">
                <Buildings size={32} className="text-[#FF6600]" />
              </div>
              <h2 className="text-xl font-bold mb-2">Footer Information</h2>
              <p className="text-zinc-500">
                Add your business details for email compliance
              </p>
            </div>
            
            <div>
              <Label className="mb-2 block">Business Name *</Label>
              <Input
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Your Company Name"
                className="border-2"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Business Address</Label>
              <Input
                value={footerAddress}
                onChange={(e) => setFooterAddress(e.target.value)}
                placeholder="123 Main St, City, State ZIP"
                className="border-2"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Required by CAN-SPAM for marketing emails
              </p>
            </div>
            
            <div>
              <Label className="mb-2 block">Additional Footer Text (optional)</Label>
              <Input
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                placeholder="e.g., Licensed & Insured | Family-owned since 2010"
                className="border-2"
              />
            </div>
            
            <div className="flex justify-between gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="border-2"
              >
                <ArrowLeft size={18} className="mr-2" />
                Back
              </Button>
              <Button
                onClick={saveFooter}
                disabled={loading || !businessName.trim()}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
              >
                {loading ? (
                  <SpinnerGap size={18} className="animate-spin" />
                ) : (
                  <>
                    Next: Finish
                    <ArrowRight size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Brand Kit Complete!</h2>
              <p className="text-zinc-500">
                We&apos;ll now generate your branded email templates
              </p>
            </div>
            
            {/* Summary */}
            <div className="border-2 border-zinc-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-3">
                {logoUrl && (
                  <div className="w-12 h-12 bg-white rounded border border-zinc-200 flex items-center justify-center overflow-hidden">
                    <img 
                      src={logoUrl} 
                      alt="Logo" 
                      className="max-w-full max-h-full object-contain"
                      data-testid="summary-logo"
                    />
                  </div>
                )}
                <div>
                  <p className="font-medium">{businessName}</p>
                  <p className="text-sm text-zinc-500">{footerAddress || "No address set"}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded border border-zinc-200" style={{ backgroundColor: primaryColor }} />
                  <span className="text-sm">
                    <span className="text-xs text-zinc-400">Primary: </span>
                    <span className="font-mono">{primaryColor}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded border border-zinc-200" style={{ backgroundColor: accentColor }} />
                  <span className="text-sm">
                    <span className="text-xs text-zinc-400">Accent: </span>
                    <span className="font-mono">{accentColor}</span>
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 flex items-center gap-2">
                <CheckCircle size={16} />
                3 professional templates will be created: Newsletter, Promotional, Announcement
              </p>
            </div>
            
            <div className="flex justify-between gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setStep(3)}
                className="border-2"
              >
                <ArrowLeft size={18} className="mr-2" />
                Back
              </Button>
              <Button
                onClick={generateTemplates}
                disabled={loading}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
              >
                {loading ? (
                  <SpinnerGap size={18} className="animate-spin" />
                ) : (
                  <>
                    <CheckCircle size={18} className="mr-2" />
                    Generate Templates
                  </>
                )}
              </Button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <Card className="border-2 border-zinc-200 max-w-lg mx-auto">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="text-xs">
            Step {step} of {totalSteps}
          </Badge>
          <span className="text-xs text-zinc-500">Brand Kit Setup</span>
        </div>
        <Progress value={progress} className="h-2" />
      </CardHeader>
      <CardContent>
        {initialLoading ? (
          <div className="py-12 text-center">
            <SpinnerGap size={32} className="mx-auto text-[#FF6600] animate-spin mb-3" />
            <p className="text-zinc-500">Checking existing branding...</p>
          </div>
        ) : (
          renderStep()
        )}
      </CardContent>
    </Card>
  );
}

/**
 * ColorPicker - Simple inline color picker component
 */
export function ColorPicker({ value, onChange, label, palette = [] }) {
  return (
    <div className="space-y-2">
      {label && <Label className="block">{label}</Label>}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg border-2 border-zinc-200"
          style={{ backgroundColor: value }}
        />
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-28 font-mono text-sm"
        />
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 p-0 border-0 cursor-pointer rounded"
        />
      </div>
      {palette.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {palette.slice(0, 6).map((color, idx) => (
            <button
              key={idx}
              onClick={() => onChange(color)}
              className={`w-6 h-6 rounded border transition-all ${
                value === color ? "border-black scale-110" : "border-zinc-200 hover:border-zinc-400"
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
